/**
 * 文件路径: D:\XXY\MeiShan_Project\frontend\shared\api\base.ts
 * 通用API服务基类
 */

import { httpClient } from './http'
import type { PaginatedResponse, PaginationParams } from '../types'
import type { AxiosProgressEvent } from 'axios'

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
type UploadProgressHandler = (progressEvent: AxiosProgressEvent) => void

/**
 * 通用API服务基类
 * 提供标准的CRUD操作方法
 */
export abstract class BaseApiService<T, CreateData = Partial<T>, UpdateData = Partial<T>> {
  protected abstract readonly endpoint: string

  /**
   * 获取列表（支持分页和筛选）
   * @param params - 查询参数
   * @returns 分页响应数据
   */
  async getList(params?: PaginationParams & Record<string, unknown>): Promise<PaginatedResponse<T>> {
    return await httpClient.get<PaginatedResponse<T>>(this.endpoint, { params })
  }

  /**
   * 获取单个资源
   * @param id - 资源ID
   * @returns 资源数据
   */
  async getById(id: string | number): Promise<T> {
    return await httpClient.get<T>(`${this.endpoint}${id}/`)
  }

  /**
   * 创建新资源
   * @param data - 创建数据
   * @returns 创建的资源数据
   */
  async create(data: CreateData): Promise<T> {
    return await httpClient.post<T>(this.endpoint, data)
  }

  /**
   * 更新资源
   * @param id - 资源ID
   * @param data - 更新数据
   * @returns 更新后的资源数据
   */
  async update(id: string | number, data: UpdateData): Promise<T> {
    return await httpClient.put<T>(`${this.endpoint}${id}/`, data)
  }

  /**
   * 部分更新资源
   * @param id - 资源ID  
   * @param data - 部分更新数据
   * @returns 更新后的资源数据
   */
  async partialUpdate(id: string | number, data: Partial<UpdateData>): Promise<T> {
    return await httpClient.patch<T>(`${this.endpoint}${id}/`, data)
  }

  /**
   * 删除资源
   * @param id - 资源ID
   */
  async delete(id: string | number): Promise<void> {
    await httpClient.delete(`${this.endpoint}${id}/`)
  }

  /**
   * 批量删除资源
   * @param ids - 资源ID数组
   */
  async bulkDelete(ids: (string | number)[]): Promise<void> {
    return await httpClient.post(`${this.endpoint}bulk-delete/`, { ids })
  }

  /**
   * 搜索资源
   * @param query - 搜索关键词
   * @param params - 额外查询参数
   * @returns 分页响应数据
   */
  async search(query: string, params?: PaginationParams & Record<string, unknown>): Promise<PaginatedResponse<T>> {
    return await httpClient.get<PaginatedResponse<T>>(`${this.endpoint}search/`, {
      params: { q: query, ...params }
    })
  }

  /**
   * 获取资源统计信息
   * @param params - 统计参数
   * @returns 统计数据
   */
  async getStats(params?: Record<string, unknown>): Promise<Record<string, unknown>> {
    return await httpClient.get(`${this.endpoint}stats/`, { params })
  }

  /**
   * 执行自定义操作
   * @param id - 资源ID
   * @param action - 操作名称
   * @param data - 操作数据
   * @returns 操作结果
   */
  async performAction(id: string | number, action: string, data?: unknown): Promise<unknown> {
    return await httpClient.post(`${this.endpoint}${id}/${action}/`, data)
  }

  /**
   * 导出数据
   * @param params - 导出参数
   * @param format - 导出格式 (csv, xlsx, pdf)
   * @returns 文件blob
   */
  async export(params?: Record<string, unknown>, format: 'csv' | 'xlsx' | 'pdf' = 'csv'): Promise<Blob> {
    const response = await httpClient.getAxiosInstance().get(`${this.endpoint}export/`, {
      params: { format, ...params },
      responseType: 'blob'
    })
    return response.data
  }

  /**
   * 导入数据
   * @param file - 导入文件
   * @param onUploadProgress - 上传进度回调
   * @returns 导入结果
   */
  async import(file: File, onUploadProgress?: UploadProgressHandler): Promise<unknown> {
    return await httpClient.upload(`${this.endpoint}import/`, file, onUploadProgress)
  }
}